import { ref } from 'vue';
import { $fetch } from 'ofetch';
import type { ExclusiveBlock } from '~/types/exclusives';

export interface AutocompleteProject {
  project_id: string;
  // Raw postgres projects.id (no geniemap COALESCE). Used as the canonical
  // training-link key; URL routing still uses project_id.
  internal_id: string;
  title: string;
  image?: string;
  images?: string[];
  developer?: string;
  exclusive?: ExclusiveBlock;
}

export interface AutocompleteArea {
  // Real districts use the district name as id; curated "virtual" areas use a
  // `virtual:<key>` token and carry the districts they expand to.
  id: string | number;
  title: string;
  projects_count?: number;
  virtual?: boolean;
  districts?: string[];
  emirate?: string;
}

export interface AutocompleteDeveloper {
  id?: number;
  name: string;
}

export interface AutocompleteSuggestion {
  id: string;
  title: string;
  type: 'project' | 'area' | 'developer';
  image?: string;
  developer?: string;
}

export interface SmartSearchGroupedResult {
  // `id` keeps the COALESCE(geniemap_id, postgres.id) form so existing URL
  // builders (Filters, SmartSearchDropdown) stay unchanged. `internalId` is
  // the raw postgres projects.id — use this for training links and any
  // identifier that needs to be stable across geniemap presence.
  projects: Array<{
    id: string;
    internalId: string;
    title: string;
    image?: string;
    images?: string[];
    developer?: string;
    // Only true for viewers allowed to see exclusives (the API omits the block otherwise).
    isExclusive: boolean;
  }>;
  developers: Array<{ id: string; name: string }>;
  areas: Array<{
    id: string | number;
    title: string;
    projects_count?: number;
    virtual?: boolean;
    districts?: string[];
    emirate?: string;
  }>;
}

export interface AutocompleteResponse {
  status: string;
  message: string;
  data: {
    projects: AutocompleteProject[];
    developers: AutocompleteDeveloper[];
    emirates: unknown[];
    areas: AutocompleteArea[];
  };
}

export function mapResponseToGrouped(response: AutocompleteResponse): SmartSearchGroupedResult {
  const projects = (response.data?.projects || []).map((p) => ({
    id: p.project_id,
    internalId: p.internal_id,
    title: p.title,
    image: p.image,
    images: p.images ?? [],
    developer: p.developer,
    isExclusive: p.exclusive?.isExclusive === true,
  }));
  const apiDevelopers = (response.data?.developers || [])
    .map((d) => ({
      id: String(d.id ?? d.name),
      name: d.name || '',
    }))
    .filter((d) => d.name);

  // Fallback: derive developers from matched projects when autocomplete API omits developer items.
  const projectDevelopers = projects
    .filter((p) => p.developer)
    .map((p) => ({
      id: String(p.developer),
      name: String(p.developer),
    }));

  const developersMap = new Map<string, { id: string; name: string }>();
  [...apiDevelopers, ...projectDevelopers].forEach((d) => {
    const key = d.name.trim().toLowerCase();
    if (!key) return;
    if (!developersMap.has(key)) {
      developersMap.set(key, d);
    }
  });

  const developers = Array.from(developersMap.values());

  const areas = (response.data?.areas || []).map((a) => ({
    id: a.id,
    title: a.title,
    projects_count: a.projects_count,
    virtual: a.virtual,
    districts: a.districts,
    emirate: a.emirate,
  }));
  return { projects, developers, areas };
}

function hasAnyResults(g: SmartSearchGroupedResult): boolean {
  return g.projects.length > 0 || g.developers.length > 0 || g.areas.length > 0;
}

export const usePropertyAutocomplete = () => {
  const authStore = useAuthStore();
  const suggestions = ref<AutocompleteSuggestion[]>([]);
  const grouped = ref<SmartSearchGroupedResult>({
    projects: [],
    developers: [],
    areas: [],
  });
  const isFallbackSuggestions = ref(false);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Out-of-order guard. Each keystroke fires its own request; without this,
  // a slower earlier request (e.g. "terra", started when the user pauses at
  // the space) can resolve AFTER the later "terra casa" request and clobber
  // the correct results. We tag every call with a monotonic id, abort the
  // previous in-flight request, and only let the latest call write state.
  let latestRequestId = 0;
  let inflightController: AbortController | null = null;

  const searchProperties = async (query: string) => {
    const requestId = ++latestRequestId;
    // Cancel the previous keystroke's request so its response can never win.
    inflightController?.abort();
    const controller = new AbortController();
    inflightController = controller;

    const isStale = () => requestId !== latestRequestId;

    if (!query || query.trim().length < 2) {
      suggestions.value = [];
      grouped.value = { projects: [], developers: [], areas: [] };
      isFallbackSuggestions.value = false;
      return;
    }

    isLoading.value = true;
    error.value = null;
    isFallbackSuggestions.value = false;

    const fetchAutocomplete = async (q: string) =>
      $fetch<AutocompleteResponse>('/api/properties/v2/search/autocomplete', {
        params: { q: q.trim() },
        signal: controller.signal,
        // Signed-in agents get the `exclusive` block and catalogue-only exclusives (ID-117).
        headers: authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {},
      });

    const setGroupedAndSuggestions = (g: SmartSearchGroupedResult) => {
      grouped.value = g;
      const allSuggestions: AutocompleteSuggestion[] = [];
      g.projects.forEach((p) => {
        allSuggestions.push({
          id: p.id,
          title: p.title,
          type: 'project',
          image: p.image,
          developer: p.developer,
        });
      });
      g.areas.forEach((a) => {
        if (a.projects_count !== 0) {
          allSuggestions.push({ id: String(a.id), title: a.title, type: 'area' });
        }
      });
      suggestions.value = allSuggestions;
    };

    try {
      const trimmed = query.trim();
      const response = await fetchAutocomplete(trimmed);
      if (isStale()) return; // a newer keystroke superseded this request
      const result = mapResponseToGrouped(response);

      if (hasAnyResults(result)) {
        setGroupedAndSuggestions(result);
        return;
      }

      const fallbackQuery = trimmed.length > 2 ? trimmed.slice(0, 2) : trimmed;
      if (fallbackQuery.length < 2) {
        setGroupedAndSuggestions(result);
        return;
      }

      const fallbackResponse = await fetchAutocomplete(fallbackQuery);
      if (isStale()) return;
      const fallbackResult = mapResponseToGrouped(fallbackResponse);
      if (hasAnyResults(fallbackResult)) {
        isFallbackSuggestions.value = true;
        setGroupedAndSuggestions(fallbackResult);
      } else {
        setGroupedAndSuggestions(result);
      }
    } catch (err) {
      // An aborted (superseded) request rejects — that's expected, not an error.
      if (controller.signal.aborted || isStale()) return;
      console.error('Error fetching autocomplete suggestions:', err);
      error.value = 'Failed to load suggestions';
      suggestions.value = [];
      grouped.value = { projects: [], developers: [], areas: [] };
    } finally {
      // Only the latest request may flip the spinner off, so an aborted earlier
      // request doesn't clear loading while the current one is still fetching.
      if (!isStale()) isLoading.value = false;
    }
  };

  return {
    suggestions,
    grouped,
    isFallbackSuggestions,
    isLoading,
    error,
    searchProperties,
  };
};
